/**
 * useClientLogger hook + context provider glue.
 *
 * Re-exports the singleton client-logger API in a React-friendly way
 * (live-updating logs array via subscription).
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import {
  clearBuffer,
  flush as rawFlush,
  getBuffer,
  isEnabled,
  sendToSupport as rawSendToSupport,
  setEnabled as rawSetEnabled,
  subscribe,
  type ClientLogEntry,
} from "@/lib/client-logger";

export type { ClientLogEntry };

export interface ClientLoggerContextValue {
  enabled: boolean;
  logs: ClientLogEntry[];
  setEnabled: (enabled: boolean) => void;
  clear: () => void;
  flush: (note?: string) => void;
  sendToSupport: (note?: string) => void;
}

function getSnapshot(): ClientLogEntry[] {
  return getBuffer();
}

function subscribeToBuffer(callback: () => void): () => void {
  return subscribe(callback);
}

export function useClientLogger(): ClientLoggerContextValue {
  const logs = useSyncExternalStore(subscribeToBuffer, getSnapshot, getSnapshot);

  const [enabledState, setEnabledState] = useState<boolean>(() => isEnabled());

  // Keep local state in sync if setEnabled is called from elsewhere (e.g. panel toggle)
  useEffect(() => {
    const check = () => setEnabledState(isEnabled());
    const unsub = subscribe(check);
    return unsub;
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    rawSetEnabled(next);
    setEnabledState(next);
  }, []);

  const clear = useCallback(() => {
    clearBuffer();
  }, []);

  const flush = useCallback((note?: string) => {
    rawFlush(note);
  }, []);

  const sendToSupport = useCallback((note?: string) => {
    rawSendToSupport(note);
  }, []);

  return useMemo(() => ({
    enabled: enabledState,
    logs,
    setEnabled,
    clear,
    flush,
    sendToSupport,
  }), [enabledState, logs, setEnabled, clear, flush, sendToSupport]);
}
